import { useCallback, useEffect, useMemo } from 'react';
import { Device, useChannelContext, useSecureContext } from 'scbk-services';
import { useDevice } from 'scbk-ui-web';
import { secureKeypadManager } from '../manager/SecureKeypadManager';
import { useAstxContext } from '../providers/AstxKeypadProvider';
import { useNFilterPublicKeyContext } from '../providers/NFilterPublicKeyProvider';
import { useSecureKeypadStore } from '../stores/useSecureKeypadStore';
import { SecureKeypadFieldId, SecureKeypadFieldOption } from '../types/types';
import { setFieldValueByElement } from 'scbk-shared-module/modules/Form/hooks/useField';

export type UseSecureKeypadProps = {
  options: SecureKeypadFieldOption[];
};

export type UseSecureKeypadReturn = {
  ref: ((el: HTMLInputElement | null) => void) | undefined;
  init: () => void;
  open: (fieldId: SecureKeypadFieldId, option?: SecureKeypadFieldOption) => void;
  close: (fieldId: SecureKeypadFieldId) => void;
  clear: (fieldId?: SecureKeypadFieldId) => void;
  register: (option: SecureKeypadFieldOption) => void;
  unregister: (fieldId: SecureKeypadFieldId) => void;
  setKeypadEncData: () => void;
  driverId: () => 'native' | 'virtual' | 'astx2' | undefined;
};

/**
 * 보안키패드 처리 hook
 * @param options 보안키패드 대상 필드 초기 옵션
 * @returns
 */
const useSecureKeypad = (
  options?: SecureKeypadFieldOption[],
  virtualOnly: boolean = false,
  checkAstxInstall: boolean = false,
  scrollLock: boolean = false,
): UseSecureKeypadReturn => {
  const { channel } = useChannelContext();
  const { setKeypadContext, getContext, resetKeypadSecureContext } = useSecureContext();
  const { publicKey } = useNFilterPublicKeyContext(); // public key provider
  const { setScroll, setPopupScroll } = useDevice();
  const setClear = useSecureKeypadStore((state) => state.setClear);
  const resetClear = useSecureKeypadStore((state) => state.resetClear);
  const { isInstalled } = useAstxContext();

  const ctx = useMemo(() => {
    console.log(
      '### channel : ',
      channel,
      '### options : ',
      options,
      '### virtualOnly : ',
      virtualOnly,
      '### isInstalled : ',
      isInstalled,
      '### checkAstxInstall : ',
      checkAstxInstall,
    );

    return {
      channel,
      device: Device.APP,
      options,
      virtualOnly: checkAstxInstall && !isInstalled,
    };
  }, [channel, virtualOnly, isInstalled, checkAstxInstall]);

  const manager = useMemo(() => secureKeypadManager(), []);

  /**
   * 보안키패드 함수 획득
   */
  const driver = useMemo(() => {
    console.log('### ctx : ', ctx);
    const driver = manager.resolveDriver(ctx);
    return driver;
  }, [ctx]);

  /**
   * 키패드 드라이버 초기화 수행
   */
  const init = useCallback(async () => {
    if (driver) {
      console.log('### driver :', driver, driver?.id === 'native');

      driver?.init(ctx);
    }
  }, [driver]);

  /**
   * 필드 ID 기반 ref 처리
   */
  const ref = useCallback(
    (el: HTMLInputElement | null) => {
      //언마운트 처리
      if (!el) {
        return;
      }

      //*****************************
      //초기화 중복 방지 코드
      //*****************************
      const init = el.getAttribute('data-keypad-init');
      if (init === 'true') {
        return;
      }

      const id = el.getAttribute('id');
      if (!id) {
        console.warn('필수 속성[id] 누락');
        return;
      }

      const name = el.getAttribute('name') ?? '';
      if (name === '') {
        el.setAttribute('name', id);
      }

      const option = driver?.getOptions?.(id as SecureKeypadFieldId);

      //*****************************
      //네이티브 키패드 nfilter public key 설정
      //*****************************
      if (driver?.id == 'native') {
        el.setAttribute('data-nf-public-key', publicKey);
      }

      if (option) {
        option.setEncData = async () => {
          await setKeypadEncData();
        };

        //*****************************
        //결함번호 115755 대응 처리
        //*****************************
        el.setAttribute('data-secure-keypad', 'true');

        // 확장 E2E 여부
        const isExtE2E = el.getAttribute('data-is-ext-e2e') ?? false;

        console.log('### isExtE2E : ', isExtE2E);

        if (driver?.id === 'astx2') {
          driver?.register(el, option);
        } else {
          // 확장 E2E 아닐때만 등록
          if (!isExtE2E) {
            driver?.register(el, option, setScroll, setPopupScroll, scrollLock);
          }
        }
      }

      el.setAttribute('data-keypad-init', 'true');
    },
    [driver],
  );

  /**
   * 키패드 오픈
   * @param fieldId 필드 ID
   */
  const open = useCallback(
    (fieldId: string) => {
      if (driver) {
        console.log('### open fieldId : ', fieldId);
        driver.open?.(fieldId);
      }
    },
    [driver],
  );

  /**
   * 키패드를 닫는다.
   * @param fieldId 키패드 필드 ID
   */
  const close = useCallback(
    (fieldId: string) => {
      if (driver) {
        driver.close?.(fieldId);
      }
    },
    [driver],
  );

  /**
   *
   */
  const clear = useCallback(
    (fieldId?: string) => {
      if (!driver) {
        return;
      }

      driver.clear?.(fieldId);
      if (fieldId) {
        const el = document.getElementById(fieldId) as HTMLInputElement;
        const isExtE2E = el.getAttribute('data-is-ext-e2e') ?? false;
        if (el && !isExtE2E) {
          setFieldValueByElement?.(el, '');
        }
      } else {
        driver.forEachInput?.((el) => {
          const isExtE2E = el.getAttribute('data-is-ext-e2e') ?? false;
          if (el && !isExtE2E) {
            setFieldValueByElement?.(el as HTMLInputElement, '');
          }
        });
        resetKeypadSecureContext();
      }
    },
    [driver, resetKeypadSecureContext],
  );

  const register = useCallback((option: SecureKeypadFieldOption) => {
    console.log(option);
  }, []);

  const unregister = useCallback((fieldId: SecureKeypadFieldId) => {
    console.log(fieldId);
  }, []);

  const setKeypadEncData = useCallback(async () => {
    const encData = (await driver?.getEncData?.()) ?? {};
    setKeypadContext({ ...encData });

    console.log('### getContext : ', getContext());
  }, [driver]);

  const driverId = () => {
    if (driver) {
      return driver.id;
    }
  };

  // useEffect(() => {
  //   console.log('### checkAstxInstall : ', checkAstxInstall);
  //   if (!checkAstxInstall) {
  //     return;
  //   }

  //   const check = async () => {
  //     let installed = await astxIsInstall();
  //     setVirtualOnlyByInstall(!installed);
  //   };

  //   check();
  // }, [checkAstxInstall, astxIsInstall]);

  useEffect(() => {
    setClear(clear);

    return () => {
      resetClear();
    };
  }, [clear, setClear, resetClear]);

  return {
    ref,
    init,
    open,
    close,
    register,
    unregister,
    setKeypadEncData,
    driverId,
    clear,
  };
};

export { useSecureKeypad };
